import { z } from "zod";

/** Every reason a movement can record. "initial" is written only by the system. */
export const stockMovementReasonSchema = z.enum([
  "initial",
  "receive",
  "return",
  "sell",
  "adjust",
  "correction",
]);
export type StockMovementReason = z.infer<typeof stockMovementReasonSchema>;

/** Reasons an admin may select when adjusting stock (excludes system-only "initial"). */
export const adjustStockReasonSchema = z.enum([
  "receive",
  "return",
  "sell",
  "adjust",
  "correction",
]);
export type AdjustStockReason = z.infer<typeof adjustStockReasonSchema>;

/** A single append-only stock movement as returned by the API. */
export const stockMovementSchema = z
  .object({
    id: z.string(),
    productId: z.string(),
    delta: z.number().int(),
    reason: stockMovementReasonSchema,
    /** Resulting on-hand quantity after this movement (self-verifying trail). */
    quantityAfter: z.number().int().nonnegative(),
    note: z.string().nullable(),
    createdAt: z.string(),
    createdBy: z.string().nullable(),
  })
  .strict();
export type StockMovement = z.infer<typeof stockMovementSchema>;

/** Reasons that may only increase stock / only decrease stock. */
const POSITIVE_ONLY: AdjustStockReason[] = ["receive", "return"];
const NEGATIVE_ONLY: AdjustStockReason[] = ["sell"];

export const adjustStockInputSchema = z
  .object({
    delta: z.number().int(),
    reason: adjustStockReasonSchema,
    note: z.string().max(500).optional(),
  })
  .strict()
  .superRefine((v, ctx) => {
    if (v.delta === 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "delta must be non-zero",
        path: ["delta"],
      });
      return;
    }
    if (POSITIVE_ONLY.includes(v.reason) && v.delta < 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `"${v.reason}" must increase stock (use a positive delta)`,
        path: ["delta"],
      });
    }
    if (NEGATIVE_ONLY.includes(v.reason) && v.delta > 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `"${v.reason}" must decrease stock (use a negative delta)`,
        path: ["delta"],
      });
    }
  });
export type AdjustStockInput = z.infer<typeof adjustStockInputSchema>;
